import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

export default function TutorTestPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState('instructions'); // 'instructions', 'preview', 'test', 'confirmation'
  const [test, setTest] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [dailyRoomUrl, setDailyRoomUrl] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTest();
  }, [token]);

  useEffect(() => {
    if (step === 'test' && submission && submission.started_at && test) {
      const timer = setInterval(() => {
        const elapsed = Math.floor((new Date() - new Date(submission.started_at)) / 1000);
        const remaining = (test.duration_minutes * 60) - elapsed;
        if (remaining <= 0) {
          handleAutoSubmit();
          clearInterval(timer);
        } else {
          setTimeRemaining(remaining);
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [step, submission, test]);

  const loadTest = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/test/${token}/`);
      if (response.data.success) {
        setTest(response.data.data.test);
        if (response.data.data.submission_exists) {
          setError('This test has already been taken');
        }
      } else {
        setError(response.data.message || 'Failed to load test');
      }
    } catch (error) {
      console.error('Error loading test:', error);
      setError(error.response?.data?.message || 'Failed to load test');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTest = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/test/${token}/start/`);
      if (response.data.success) {
        setSubmission(response.data.data.submission);
        if (response.data.data.daily_room_url) {
          const url = new URL(response.data.data.daily_room_url);
          url.searchParams.set('enableCamera', 'true');
          url.searchParams.set('enableMic', 'true');
          url.searchParams.set('enableScreenShare', 'true');
          setDailyRoomUrl(url.toString());
        }
        setStep('preview');
      } else {
        alert(response.data.message || 'Failed to start test');
      }
    } catch (error) {
      console.error('Error starting test:', error);
      alert(error.response?.data?.message || 'Failed to start test');
    }
  };

  const handleStartQuestions = async () => {
    // Start the timer when they begin answering questions
    try {
      const response = await axios.post(`${API_URL}/api/test/${token}/start-timer/`);
      if (response.data.success) {
        // Update submission with new started_at
        setSubmission(response.data.data);
        setStep('test');
      } else {
        alert(response.data.message || 'Failed to start timer');
      }
    } catch (error) {
      console.error('Error starting timer:', error);
      alert(error.response?.data?.message || 'Failed to start timer');
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < test.questions.length - 1) {
      const confirmMessage = `Are you ready to move to the next question?\n\nPlease ensure:\n• Your camera is ON\n• Your microphone is ON\n• Screen sharing is ACTIVE (entire screen shared)\n• Recording is ON\n\nIf any of these are not enabled, you may be disqualified.`;
      
      if (window.confirm(confirmMessage)) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      }
    }
  };

  const handleSubmitTest = async () => {
    if (!window.confirm('Are you sure you want to submit the test? You cannot go back.')) {
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/test/${token}/submit/`);
      if (response.data.success) {
        setStep('confirmation');
      } else {
        alert(response.data.message || 'Failed to submit test');
      }
    } catch (error) {
      console.error('Error submitting test:', error);
      alert(error.response?.data?.message || 'Failed to submit test');
    }
  };

  const handleAutoSubmit = async () => {
    try {
      await axios.post(`${API_URL}/api/test/${token}/submit/`);
      setStep('confirmation');
    } catch (error) {
      console.error('Error auto-submitting test:', error);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Loading test...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Render left content based on step
  const renderLeftContent = () => {
    if (step === 'instructions') {
      return (
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-2xl w-full">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">🎓</div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Test</h1>
            <p className="text-gray-600 text-lg">Welcome! Let's get started</p>
          </div>
          
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6 mb-6 border border-indigo-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{test.title}</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <div className="bg-indigo-100 rounded-full p-2">
                  <span className="text-2xl">⏱️</span>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Duration</p>
                  <p className="font-semibold text-gray-900">{test.duration_minutes} minutes</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-purple-100 rounded-full p-2">
                  <span className="text-2xl">❓</span>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Questions</p>
                  <p className="font-semibold text-gray-900">{test.total_questions} questions</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-xl font-semibold mb-3 text-gray-900 flex items-center gap-2">
              <span className="text-2xl">📋</span>
              Instructions
            </h3>
            <div className="bg-gray-50 border border-gray-200 p-5 rounded-lg text-gray-700 leading-relaxed prose max-w-none" dangerouslySetInnerHTML={{ __html: test.instructions }} />
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-400 p-5 mb-6 rounded-r-lg">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <p className="font-semibold text-amber-900 mb-1">Important Reminder</p>
                <p className="text-sm text-amber-800 leading-relaxed">
                  This test can only be taken <strong>once</strong>. Please ensure your camera, microphone, and screen sharing are working properly before starting. You will not be able to go back to previous questions once you proceed.
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={handleStartTest}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 rounded-lg font-semibold text-lg hover:from-indigo-700 hover:to-purple-700 transform hover:scale-[1.02] transition-all shadow-lg hover:shadow-xl"
          >
            🚀 Start Test
          </button>
        </div>
      );
    }

    if (step === 'preview') {
      return (
        <div className="bg-white rounded-xl shadow-xl p-6">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
              <span className="text-3xl">🎥</span>
              Setup Your Video Meeting
            </h1>
            <p className="text-gray-600">Please configure your video settings before starting the test</p>
          </div>
          
          <div className="space-y-4 mb-6">
            <div className="bg-red-50 border-l-4 border-red-500 p-5 rounded-r-lg shadow-sm">
              <h3 className="font-bold text-red-900 mb-3 flex items-center gap-2 text-lg">
                <span className="text-2xl">⚠️</span>
                Critical Requirements
              </h3>
              <p className="text-sm text-red-800 mb-4 leading-relaxed">
                Please join the video meeting on the right side and ensure <strong>all</strong> of the following are enabled before proceeding:
              </p>
              <ul className="text-sm text-red-800 space-y-3 list-none">
                <li className="flex items-center gap-3">
                  <span className="text-xl">📹</span>
                  <div>
                    <strong>Camera</strong> must be ON and showing your face clearly
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <span className="text-xl">🎤</span>
                  <div>
                    <strong>Microphone</strong> must be ON and working properly
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <span className="text-xl">🖥️</span>
                  <div>
                    <strong>Screen Sharing</strong> must be ACTIVE - share your <strong>entire screen</strong> (not just a window)
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <span className="text-xl">🔴</span>
                  <div>
                    <strong>Recording</strong> must be ON (click the record button in the meeting)
                  </div>
                </li>
              </ul>
              <div className="mt-4 p-3 bg-red-100 rounded-lg border border-red-200">
                <p className="text-sm text-red-900 font-semibold">
                  ⚠️ If any of these are not enabled, you may be disqualified from the interview process.
                </p>
              </div>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-5 rounded-r-lg shadow-sm">
              <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <span className="text-xl">📝</span>
                Step-by-Step Instructions
              </h3>
              <ol className="text-sm text-blue-800 space-y-3 list-decimal list-inside ml-2">
                <li className="leading-relaxed">Join the video meeting displayed on the right side of your screen</li>
                <li className="leading-relaxed">Click the <strong>camera icon</strong> in the meeting controls to enable your camera</li>
                <li className="leading-relaxed">Click the <strong>microphone icon</strong> to enable your microphone</li>
                <li className="leading-relaxed">Click the <strong>screen share button</strong> and select <strong>your entire screen</strong> (not just a window) to share</li>
                <li className="leading-relaxed">Click the <strong>record button</strong> (usually a red circle) to start recording your session</li>
                <li className="leading-relaxed">Verify all icons show as active/enabled in the meeting controls</li>
              </ol>
            </div>

            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 p-5 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <span className="text-xl">ℹ️</span>
                Test Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-xl">⏱️</span>
                  <div>
                    <p className="text-xs text-gray-600">Duration</p>
                    <p className="font-semibold text-gray-900">{test.duration_minutes} minutes</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xl">❓</span>
                  <div>
                    <p className="text-xs text-gray-600">Questions</p>
                    <p className="font-semibold text-gray-900">{test.total_questions} questions</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={handleStartQuestions}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 rounded-lg font-semibold text-lg hover:from-indigo-700 hover:to-purple-700 transform hover:scale-[1.02] transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
          >
            <span>✅</span>
            I'm Ready - Start Questions
          </button>
        </div>
      );
    }

    if (step === 'test') {
      const currentQuestion = test.questions[currentQuestionIndex];
      const isLastQuestion = currentQuestionIndex === test.questions.length - 1;
      const progressPercentage = ((currentQuestionIndex + 1) / test.questions.length) * 100;

      return (
        <div className="bg-white rounded-xl shadow-xl p-6">
          {/* Header with Timer and Progress */}
          <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="bg-red-100 rounded-full p-2">
                <span className="text-xl">⏱️</span>
              </div>
              <div>
                <p className="text-xs text-gray-600">Time Remaining</p>
                <p className="text-lg font-bold text-gray-900">{formatTime(timeRemaining)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-indigo-100 rounded-full p-2">
                <span className="text-xl">❓</span>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-600">Question</p>
                <p className="text-lg font-bold text-gray-900">
                  {currentQuestionIndex + 1} of {test.questions.length}
                </p>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-indigo-600 to-purple-600 h-3 rounded-full transition-all duration-300 shadow-sm"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              {Math.round(progressPercentage)}% Complete
            </p>
          </div>

          {/* Question Content */}
          <div className="mb-6">
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-5 mb-4 border border-indigo-100">
              <div className="text-2xl font-bold text-gray-900 leading-relaxed prose max-w-none" dangerouslySetInnerHTML={{ __html: currentQuestion.question_text }} />
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
              <p className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                <span className="text-lg">⚠️</span>
                Quick Reminder
              </p>
              <p className="text-sm text-amber-800 leading-relaxed">
                Please ensure your <strong>camera</strong>, <strong>microphone</strong>, <strong>screen sharing (entire screen)</strong>, and <strong>recording</strong> are all ON. 
                If any of these are not enabled, you may be disqualified.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            {!isLastQuestion ? (
              <button
                onClick={handleNextQuestion}
                className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transform hover:scale-[1.02] transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                <span>Next Question</span>
                <span>→</span>
              </button>
            ) : (
              <button
                onClick={handleSubmitTest}
                className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-lg font-semibold hover:from-green-700 hover:to-emerald-700 transform hover:scale-[1.02] transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                <span>✅</span>
                <span>Submit Test</span>
              </button>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  // Main layout - persistent container with left and right panels
  if (step === 'instructions') {
    // Instructions page - full width, no video
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        {renderLeftContent()}
      </div>
    );
  }

  if (step === 'confirmation') {
    // Confirmation page - full width, no video
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-10 max-w-2xl w-full text-center">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-4">
              <span className="text-5xl">✅</span>
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Test Submitted Successfully!</h1>
          <p className="text-gray-600 mb-8 text-lg leading-relaxed">
            Thank you for completing the interview test. We've received your responses and video recording.
          </p>
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6 border border-indigo-100">
            <p className="text-gray-700 mb-2">
              <strong>What happens next?</strong>
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Our team will review your responses and video recording. We'll get back to you via email within the next few business days.
            </p>
          </div>
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              You can safely close this window now.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Preview and Test pages - split layout with persistent video
  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Dynamic Content - changes based on step */}
          <div>
            {renderLeftContent()}
          </div>

          {/* Right: Persistent Video Meeting - never unmounts */}
          <div className="bg-white rounded-xl shadow-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-indigo-100 rounded-full p-2">
                <span className="text-xl">🎥</span>
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                {step === 'preview' ? 'Video Meeting' : 'Video Interview'}
              </h2>
            </div>
            {dailyRoomUrl ? (
              <div className="relative bg-gray-900 rounded-lg overflow-hidden shadow-lg" style={{ paddingBottom: '56.25%', height: 0 }}>
                <iframe
                  key={dailyRoomUrl}
                  src={dailyRoomUrl}
                  allow="camera; microphone; display-capture"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    borderRadius: '8px'
                  }}
                  title="Daily.co Video Interview"
                />
              </div>
            ) : (
              <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-12 text-center">
                <div className="animate-pulse">
                  <div className="text-4xl mb-4">⏳</div>
                  <p className="text-gray-600 font-medium">Loading video room...</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

